const initialState = {
  /* Every unfinished game this account holds a seat in, or watches — the bootstrap list
     (`GET /game/`), kept up to date by whichever game's socket is open. A player may sit in
     several games at once (owner, 2026-09-22: a game of a day a round must not hold the whole
     profile), so the home page lists these and the game route picks one of them into `game`. */
  games: [],
  /* The game being viewed: the one the URL names, picked out of `games` by the route guard
     (`enterGame`), or a replay's snapshot, or basic training's scripted board. Null at home. */
  game: null,
  /* Whose board the view is drawing, when that is not the logged-in player's: a spectator who has
     picked a seat, or a replay being read as one of the players. Null the rest of the time, which
     is every ordinary game — see `selectViewerId`, which is what the panels read.

     Held here rather than in each view because it has to reach the selectors: "my cities", "my
     food", "the events addressed to me" are computed in `selectors.js` from one id, and a view
     that owns the seat locally would have to pass it through fifteen components to get there. */
  spectatingAs: null,
  /* A live spectator's two ways of looking (owner, 2026-09-29): false is "Spectator", the whole
     board as the server sends it; true is "Watching as" the seat in `spectatingAs`, with that
     seat's fog applied on the client (`selectGame`, `utils/fog`). Only a live spectator ever sets
     it — a player is fogged by the server, and a replay has its own fog eye. */
  watchingThroughFog: false,
  chatMessages: [],
  /* Reactions currently floating over the map. Deliberately not chat: they expire, they are
     capped, and nothing here is ever read back — the server stores none of it either. */
  reactions: [],
  /* Looking back at a round of a live game (requirement 35, H1–H8): `{tick, game, loading}` while
     the player has stepped the round report back, null the rest of the time. `game` is the past
     board — `{tick, tickTime, metadata}` off the step of tick T < the live tick, which holds that
     round's orders as it was sent, fogged by the server as it was then — or null while the steps
     are loading, or when that round's board has aged out of the retention window.

     Its own slot, never through `setGame`: the socket keeps writing the live game into `game`
     underneath, so the way back is instant and never stale, and the shelf is
     untouched. `selectRawGame` lays it over the live game, so every panel draws the past for free;
     `selectLiveGame` is the live game for whatever must not be fooled (the clock, the socket). */
  history: null,
  /* The round that just turned, drawn on the live board while "Round N" is announced (owner,
     2026-10-05): every order of round N-1 — the player's own, allies' and enemies' as far as they
     could see — so the player sees what happened and where the stacks now on the map came from.
     `{tick, movements}` or null, `tick` the live round it is drawn under. It rides on the
     `game_next_round` message beside the board (2026-10-17, `zvh/games/round_trail`) and is cleared,
     by its round, by `RoundFlash` when the caption goes. */
  roundTrail: null,
  /* The round's motion log (requirement 36, M1): what `process_round` did, in the order it did it,
     as this side could see it — `{tick, motion}` or null, `tick` the live round it plays under. It
     rides on the `game_next_round` message beside the trail; `utils/roundMotion` turns it into the
     timeline the canvas plays, and the Game view clears it, by its round, when the show is over. */
  roundMotion: null,
  // Every log the client has been handed, by game and by the round whose board it produced (owner,
  // 2026-10-26, `requirements/36` P2-P3): from the loaded game and from each `game_next_round`. The
  // broadcasts after an action carry none, so they cannot wipe one out; `selectRawGame` lays the
  // shown round's log back into its board. `{ "<gameId>": { "<tick>": motion } }`, a few rounds kept.
  motions: {},
}

export default initialState
